'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import { Link as IntlLink } from '@/i18n/navigation';
import { CONTACT_EMAIL, DOCS_URL } from '@/lib/constants';

import { SceneCanvas } from '../SceneCanvas';
import { usePrefersReducedMotion } from '../usePrefersReducedMotion';
import { HeroCanvas, type HeroVariant } from './HeroCanvas';

const MONO = "'JetBrains Mono', monospace";
const HELV = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const mailto = (subject: string) =>
	`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;

interface Method {
	name: string;
	tag: string;
	desc: string;
	chips: string[];
	stats: [string, string][];
}
interface Integration {
	num: string;
	tag: string;
	name: string;
	desc: string;
}
interface Workflow {
	num: string;
	name: string;
	desc: string;
	cta: string;
}
interface SelItem {
	label: string;
	value: string;
}

const lines = (titleLines: string[]) =>
	titleLines.map((line, i) => (
		<span key={i}>
			{i > 0 && <br />}
			{line}
		</span>
	));

interface ProductPageProps {
	namespace: string;
	heroVariant: HeroVariant;
	/** Valid <scene-canvas> id per method (structural, kept out of i18n). */
	methodScenes: string[];
}

/**
 * Shared product page (KYC / KYB / AML) — ported 1:1 from the design and driven
 * entirely by the given i18n `namespace`. Theme-aware: the light "paper" chrome
 * uses --color-* tokens so it flips for dark mode, while the dark HUD bands stay
 * dark in both themes (with a lifted border in dark mode via `.pp-panel`). The
 * interactive bits (status ticker, method tabs, selector) reproduce the design's
 * state as React state.
 */
export function ProductPage({
	namespace,
	heroVariant,
	methodScenes,
}: ProductPageProps) {
	const t = useTranslations(namespace);
	const reducedMotion = usePrefersReducedMotion();

	const statusSteps = t.raw('hero.statusSteps') as string[];
	const metrics = t.raw('metrics') as [string, string][];
	const methods = t.raw('methods.items') as Method[];
	const integrations = t.raw('integration.items') as Integration[];
	const workflow = t.raw('workflow.items') as Workflow[];
	const selItems = t.raw('selector.items') as SelItem[];

	const [si, setSi] = useState(0);
	const [mi, setMi] = useState(0);
	const [sel, setSel] = useState(0);

	useEffect(() => {
		if (reducedMotion) return;
		const id = setInterval(
			() => setSi((s) => (s + 1) % statusSteps.length),
			1500,
		);
		return () => clearInterval(id);
	}, [reducedMotion, statusSteps.length]);

	const m = methods[Math.max(0, Math.min(mi, methods.length - 1))];
	const cur = selItems[Math.max(0, Math.min(sel, selItems.length - 1))];

	const kicker: React.CSSProperties = {
		fontFamily: MONO,
		fontSize: '11px',
		letterSpacing: '2px',
		color: 'var(--color-muted)',
	};
	const darkBtn: React.CSSProperties = {
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
		padding: '16px 24px',
		background: '#f2f2f4',
		color: '#0a0a0b',
		border: 'none',
		cursor: 'pointer',
		fontFamily: MONO,
		fontSize: '12px',
		letterSpacing: '2px',
		textDecoration: 'none',
	};
	const ghostBtn: React.CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '16px 24px',
		background: 'transparent',
		color: '#f2f2f4',
		border: '1px solid rgba(255,255,255,.3)',
		cursor: 'pointer',
		fontFamily: MONO,
		fontSize: '12px',
		letterSpacing: '2px',
		textDecoration: 'none',
	};
	const divider = (
		<div
			style={{
				height: '1px',
				background: 'var(--color-border)',
				marginTop: '20px',
			}}
		/>
	);

	return (
		<div
			className="product-page"
			style={{
				background: 'var(--color-bg)',
				color: 'var(--color-fg)',
				fontFamily: HELV,
			}}
		>
			{/* ── HERO (dark in both themes) ── */}
			<div
				style={{
					position: 'relative',
					overflow: 'hidden',
					background: '#0a0a0b',
					color: '#f2f2f4',
					minHeight: '820px',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<HeroCanvas
					variant={heroVariant}
					className="absolute top-0 right-0 h-full w-[min(70%,900px)]"
				/>
				<div
					aria-hidden="true"
					style={{
						position: 'absolute',
						inset: 0,
						background:
							'linear-gradient(100deg, #0a0a0b 30%, rgba(10,10,11,.55) 60%, rgba(10,10,11,.15))',
						pointerEvents: 'none',
					}}
				/>
				<div
					aria-hidden="true"
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						height: '1px',
						background: 'rgba(255,255,255,.14)',
						overflow: 'hidden',
					}}
				>
					<div
						className="onekyc-sweep"
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '34%',
							height: '1px',
							background:
								'linear-gradient(90deg,transparent,rgba(255,255,255,.9),transparent)',
						}}
					/>
				</div>

				<div
					style={{
						position: 'relative',
						zIndex: 1,
						display: 'flex',
						justifyContent: 'space-between',
						gap: '16px',
						padding: 'clamp(96px,12vw,116px) clamp(18px,5vw,72px) 24px',
						fontFamily: MONO,
						fontSize: '11px',
						letterSpacing: '1.5px',
						color: 'rgba(242,242,244,.6)',
					}}
				>
					<span>{t('hud.brand')}</span>
					<span
						style={{
							border: '1px solid rgba(255,255,255,.25)',
							padding: '4px 10px',
							alignSelf: 'flex-start',
						}}
					>
						[ {t('hud.tag')} ]
					</span>
				</div>

				<div
					style={{
						position: 'relative',
						zIndex: 1,
						marginTop: 'auto',
						padding: '0 clamp(18px,5vw,72px) clamp(36px,5vw,64px)',
						maxWidth: '960px',
					}}
				>
					<div
						style={{
							fontFamily: MONO,
							fontSize: '12px',
							letterSpacing: '3px',
							color: 'rgba(242,242,244,.6)',
						}}
					>
						{t('hero.kicker')}
					</div>
					<h1
						style={{
							margin: '14px 0 0',
							fontWeight: 400,
							fontSize: 'clamp(30px,8vw,108px)',
							letterSpacing: '-.04em',
							lineHeight: 0.92,
						}}
					>
						{lines(t.raw('hero.titleLines') as string[])}
					</h1>
					<p
						style={{
							margin: '24px 0 0',
							maxWidth: '50ch',
							fontSize: 'clamp(16px,1.9vw,20px)',
							lineHeight: 1.6,
							color: 'rgba(242,242,244,.8)',
						}}
					>
						{t('hero.desc')}
					</p>
					<div
						style={{
							fontFamily: MONO,
							fontSize: '12px',
							letterSpacing: '1px',
							color: 'rgba(242,242,244,.55)',
							marginTop: '18px',
						}}
					>
						&gt; {statusSteps[si % statusSteps.length]}{' '}
						<span className="onekyc-blink" aria-hidden="true">
							_
						</span>
					</div>
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: '10px',
							marginTop: '24px',
						}}
					>
						<a href={mailto(`${t('hud.tag')} Demo Request`)} style={darkBtn}>
							{t('hero.ctaDemo')} <span aria-hidden="true">-&gt;</span>
						</a>
						<a
							href={DOCS_URL}
							target="_blank"
							rel="noopener noreferrer"
							style={ghostBtn}
						>
							{t('hero.ctaDocs')}
						</a>
					</div>
				</div>
			</div>

			{/* ── METRICS BAR ── */}
			<div style={{ borderBottom: '1px solid var(--color-border)' }}>
				<div
					style={{
						maxWidth: '1340px',
						margin: '0 auto',
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
					}}
				>
					{metrics.map(([v, l], i) => (
						<div
							key={i}
							style={{
								padding: '28px clamp(16px,2.4vw,28px)',
								borderLeft: '1px solid var(--color-border)',
							}}
						>
							<div
								style={{
									fontFamily: MONO,
									fontSize: 'clamp(22px,2.8vw,36px)',
									fontWeight: 700,
									letterSpacing: '-.02em',
									lineHeight: 1,
								}}
							>
								{v}
							</div>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '10px',
									letterSpacing: '1.5px',
									color: 'var(--color-muted)',
									marginTop: '12px',
									textTransform: 'uppercase',
								}}
							>
								{l}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* ── METHODS ── */}
			<div
				style={{
					maxWidth: '1340px',
					margin: '0 auto',
					padding: '64px clamp(18px,5vw,72px) 20px',
				}}
			>
				<div style={kicker}>{t('methods.kicker')}</div>
				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						justifyContent: 'space-between',
						alignItems: 'flex-end',
						gap: '10px 20px',
						marginTop: '8px',
					}}
				>
					<h2
						style={{
							margin: 0,
							fontWeight: 400,
							fontSize: 'clamp(28px,4.6vw,52px)',
							letterSpacing: '-.03em',
							lineHeight: 1,
						}}
					>
						{lines(t.raw('methods.titleLines') as string[])}
					</h2>
					<p
						style={{
							margin: 0,
							maxWidth: '34ch',
							fontSize: '14px',
							lineHeight: 1.6,
							color: 'var(--color-accent)',
							paddingBottom: '4px',
						}}
					>
						{t('methods.lead')}
					</p>
				</div>
				<div
					style={{
						height: '1px',
						background: 'var(--color-border)',
						marginTop: '22px',
					}}
				/>

				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: '8px',
						marginTop: '24px',
					}}
				>
					{methods.map((mt, i) => {
						const on = i === mi;
						return on ? (
							<button
								key={i}
								type="button"
								onClick={() => setMi(i)}
								style={{
									cursor: 'pointer',
									padding: '12px 18px',
									background: 'var(--color-fg)',
									color: 'var(--color-bg)',
									border: '1px solid var(--color-fg)',
									fontFamily: MONO,
									fontSize: '11.5px',
									letterSpacing: '.5px',
								}}
							>
								{mt.name}
							</button>
						) : (
							<button
								key={i}
								type="button"
								onClick={() => setMi(i)}
								className="pp-tab"
								style={{
									padding: '12px 18px',
									fontFamily: MONO,
									fontSize: '11.5px',
									letterSpacing: '.5px',
								}}
							>
								{mt.name}
							</button>
						);
					})}
				</div>

				<div
					className="pp-panel"
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))',
						gap: '2px',
						marginTop: '16px',
						background: '#0b0b0c',
					}}
				>
					<div
						style={{
							position: 'relative',
							overflow: 'hidden',
							background: '#0a0a0b',
							color: '#f2f2f4',
							minHeight: '280px',
							padding: '26px',
						}}
					>
						<SceneCanvas
							scene={methodScenes[mi % methodScenes.length]}
							className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.42]"
						/>
						<div
							aria-hidden="true"
							style={{
								position: 'absolute',
								inset: 0,
								background:
									'linear-gradient(180deg, rgba(10,10,11,.5), rgba(10,10,11,.82))',
								pointerEvents: 'none',
							}}
						/>
						<div
							style={{
								position: 'relative',
								zIndex: 1,
								display: 'flex',
								flexDirection: 'column',
								height: '100%',
							}}
						>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									fontFamily: MONO,
									fontSize: '10.5px',
									letterSpacing: '1.5px',
									color: 'rgba(242,242,244,.6)',
								}}
							>
								<span>METHOD // {m.tag}</span>
								<span>0{(mi % methodScenes.length) + 1}</span>
							</div>
							<div
								style={{
									fontSize: '26px',
									fontWeight: 500,
									letterSpacing: '-.02em',
									marginTop: 'auto',
								}}
							>
								{m.name}
							</div>
						</div>
					</div>
					<div
						style={{
							background: '#0a0a0b',
							color: '#f2f2f4',
							padding: '26px',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<p
							style={{
								margin: 0,
								fontSize: '15px',
								lineHeight: 1.65,
								color: 'rgba(242,242,244,.8)',
							}}
						>
							{m.desc}
						</p>
						<div
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '8px',
								marginTop: '20px',
							}}
						>
							{m.chips.map((ch) => (
								<span
									key={ch}
									style={{
										fontFamily: MONO,
										fontSize: '10.5px',
										letterSpacing: '.5px',
										color: 'rgba(242,242,244,.82)',
										border: '1px solid rgba(255,255,255,.2)',
										padding: '7px 11px',
									}}
								>
									{ch}
								</span>
							))}
						</div>
						<div
							style={{
								marginTop: 'auto',
								display: 'flex',
								gap: '24px',
								paddingTop: '22px',
							}}
						>
							{m.stats.map(([v, l]) => (
								<span
									key={l}
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '3px',
									}}
								>
									<span
										style={{
											fontFamily: MONO,
											fontSize: '19px',
											fontWeight: 700,
										}}
									>
										{v}
									</span>
									<span
										style={{
											fontFamily: MONO,
											fontSize: '9px',
											letterSpacing: '1.5px',
											color: 'rgba(242,242,244,.5)',
											textTransform: 'uppercase',
										}}
									>
										{l}
									</span>
								</span>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* ── INTEGRATION ── */}
			<div
				style={{
					maxWidth: '1340px',
					margin: '0 auto',
					padding: '44px clamp(18px,5vw,72px) 20px',
				}}
			>
				<div style={kicker}>{t('integration.kicker')}</div>
				<h2
					style={{
						margin: '8px 0 0',
						fontWeight: 400,
						fontSize: 'clamp(28px,4vw,44px)',
						letterSpacing: '-.025em',
						lineHeight: 1,
					}}
				>
					{t('integration.title')}
				</h2>
				{divider}
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
						gap: '14px',
						marginTop: '22px',
					}}
				>
					{integrations.map((it) => (
						<div key={it.num} className="pp-card">
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									fontFamily: MONO,
									fontSize: '10px',
									letterSpacing: '1.5px',
									color: 'var(--color-muted)',
								}}
							>
								<span>{it.num}</span>
								<span
									style={{
										border: '1px solid var(--color-border)',
										padding: '3px 8px',
									}}
								>
									{it.tag}
								</span>
							</div>
							<div
								style={{
									fontSize: '17px',
									fontWeight: 500,
									letterSpacing: '-.01em',
									marginTop: '16px',
								}}
							>
								{it.name}
							</div>
							<p
								style={{
									margin: '8px 0 0',
									fontSize: '13px',
									lineHeight: 1.55,
									color: 'var(--color-muted)',
								}}
							>
								{it.desc}
							</p>
						</div>
					))}
				</div>
			</div>

			{/* ── WORKFLOW ── */}
			<div
				style={{
					maxWidth: '1340px',
					margin: '0 auto',
					padding: '44px clamp(18px,5vw,72px) 20px',
				}}
			>
				<div style={kicker}>{t('workflow.kicker')}</div>
				<h2
					style={{
						margin: '8px 0 0',
						fontWeight: 400,
						fontSize: 'clamp(28px,4vw,44px)',
						letterSpacing: '-.025em',
						lineHeight: 1,
					}}
				>
					{t('workflow.title')}
				</h2>
				{divider}
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
						gap: '14px',
						marginTop: '22px',
					}}
				>
					{workflow.map((wf) => (
						<div
							key={wf.num}
							className="pp-panel"
							style={{
								background: '#0a0a0b',
								color: '#f2f2f4',
								padding: '26px',
								minHeight: '230px',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '11px',
									letterSpacing: '1.5px',
									color: 'rgba(242,242,244,.55)',
								}}
							>
								{wf.num}
							</div>
							<div
								style={{
									fontSize: '21px',
									fontWeight: 500,
									letterSpacing: '-.015em',
									marginTop: 'auto',
								}}
							>
								{wf.name}
							</div>
							<p
								style={{
									margin: '12px 0 0',
									fontSize: '13.5px',
									lineHeight: 1.6,
									color: 'rgba(242,242,244,.72)',
								}}
							>
								{wf.desc}
							</p>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '11px',
									letterSpacing: '1px',
									color: '#f2f2f4',
									marginTop: '18px',
								}}
							>
								{wf.cta} →
							</div>
						</div>
					))}
				</div>
			</div>

			{/* ── SELECTOR ── */}
			<div
				style={{
					maxWidth: '1340px',
					margin: '0 auto',
					padding: '44px clamp(18px,5vw,72px) 20px',
				}}
			>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))',
						gap: '30px',
						alignItems: 'center',
						borderTop: '1px solid var(--color-border)',
						paddingTop: '48px',
					}}
				>
					<div>
						<div style={kicker}>{t('selector.kicker')}</div>
						<h2
							style={{
								margin: '12px 0 0',
								fontWeight: 400,
								fontSize: 'clamp(26px,3.6vw,40px)',
								letterSpacing: '-.02em',
								lineHeight: 1.04,
							}}
						>
							{lines(t.raw('selector.titleLines') as string[])}
						</h2>
						<p
							style={{
								margin: '16px 0 0',
								maxWidth: '42ch',
								fontSize: '14px',
								lineHeight: 1.6,
								color: 'var(--color-accent)',
							}}
						>
							{t('selector.lead')}
						</p>
						<select
							value={sel}
							onChange={(e) => setSel(Number(e.target.value))}
							aria-label={t('selector.kicker')}
							style={{
								marginTop: '20px',
								width: '100%',
								maxWidth: '360px',
								border: '1px solid var(--color-fg)',
								background: 'var(--color-bg)',
								padding: '15px 16px',
								fontSize: '15px',
								color: 'var(--color-fg)',
								outline: 'none',
								cursor: 'pointer',
								fontFamily: HELV,
							}}
						>
							{selItems.map((it, i) => (
								<option key={it.label} value={i}>
									{it.label}
								</option>
							))}
						</select>
					</div>
					<div
						className="pp-panel"
						style={{
							background: '#0a0a0b',
							color: '#f2f2f4',
							padding: 'clamp(28px,4vw,44px)',
							textAlign: 'center',
						}}
					>
						<div
							style={{
								fontFamily: MONO,
								fontSize: '11px',
								letterSpacing: '1.5px',
								color: 'rgba(242,242,244,.55)',
							}}
						>
							{cur.label}
						</div>
						<div
							style={{
								fontFamily: MONO,
								fontSize: 'clamp(52px,9vw,92px)',
								fontWeight: 700,
								letterSpacing: '-.03em',
								lineHeight: 1,
								marginTop: '14px',
							}}
						>
							{cur.value}
						</div>
						<div
							style={{
								fontSize: '13.5px',
								color: 'rgba(242,242,244,.7)',
								marginTop: '10px',
							}}
						>
							{t('selector.caption')}
						</div>
					</div>
				</div>
			</div>

			{/* ── DEPLOY CTA (dark in both themes) ── */}
			<div style={{ background: '#0a0a0b', color: '#f2f2f4' }}>
				<div
					style={{
						maxWidth: '1340px',
						margin: '0 auto',
						padding: 'clamp(40px,6vw,80px) clamp(18px,5vw,72px)',
						position: 'relative',
						overflow: 'hidden',
					}}
				>
					<div
						aria-hidden="true"
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							height: '1px',
							background: 'rgba(255,255,255,.14)',
							overflow: 'hidden',
						}}
					>
						<div
							className="onekyc-sweep"
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '34%',
								height: '1px',
								background:
									'linear-gradient(90deg,transparent,rgba(255,255,255,.9),transparent)',
							}}
						/>
					</div>
					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							justifyContent: 'space-between',
							alignItems: 'flex-end',
							gap: '24px',
						}}
					>
						<div>
							<div
								style={{
									fontFamily: MONO,
									fontSize: '11px',
									letterSpacing: '2px',
									color: 'rgba(242,242,244,.5)',
								}}
							>
								{t('deploy.kicker')}
							</div>
							<h2
								style={{
									margin: '12px 0 0',
									fontWeight: 400,
									fontSize: 'clamp(28px,4.4vw,52px)',
									letterSpacing: '-.025em',
									lineHeight: 1,
								}}
							>
								{lines(t.raw('deploy.titleLines') as string[])}
							</h2>
						</div>
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
							<a
								href={mailto(`${t('hud.tag')} Sandbox Access`)}
								style={darkBtn}
							>
								{t('deploy.ctaSandbox')} <span aria-hidden="true">-&gt;</span>
							</a>
							<IntlLink
								href={{ pathname: '/', hash: 'pricing' }}
								style={ghostBtn}
							>
								{t('deploy.ctaPricing')}
							</IntlLink>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
